import { Chip, Tooltip } from '@destination/components';
import ModelAttributeCard from './ModelAttributeCard';
import { slugify } from '@/utils/text-utils';
import { useTranslation } from 'react-i18next';
import { IParameterDefinition } from '@/models/IParameterDefinition';
import { ReactNode } from 'react';

export interface IModelParametersList {
  parameters?: IParameterDefinition[];
  'data-testid'?: string;
}

export default function ModelParametersList({
  parameters,
  'data-testid': dataTestId
}: IModelParametersList) {
  const { t } = useTranslation(undefined, { keyPrefix: 'model.protocol' });
  if (!parameters || parameters.length == 0)
    return <div className="text-base">{t('no_parameters')}</div>;

  const EqualChip = ({ label }: { label: string }) => {
    return (
      <div className={`w-[120px]`}>
        <Chip label={label} className="mr-2" />
      </div>
    );
  };

  const ColoredChip = ({ label }: { label: string | ReactNode }) => {
    return (
      <div className="tw-inline-flex tw-text-[10px] tw-h-[28px] tw-items-center tw-gap-2 tw-rounded-md tw-px-4 tw-bg-[#E0F0FF] mr-2 bg-[#ffede0]">
        {label}
      </div>
    );
  };

  const parameterValueLabel = (parameter: IParameterDefinition) => {
    if (parameter.enum) {
      return (
        <Tooltip
          message={t('parameter_enum_value', {
            parameter_name: parameter.name,
            default_value: parameter.value
          })}
        >
          [
          {parameter.enum.map((value, index) => (
            <span key={index}>
              {value == parameter.value ? <b>{value}</b> : value}
              {parameter.enum && index < parameter.enum.length - 1 ? ' , ' : ''}
            </span>
          ))}
          ]
        </Tooltip>
      );
    } else if (parameter.value) {
      return (
        <Tooltip message={t('parameter_value')}>
          {parameter.value.toString()}
        </Tooltip>
      );
    }
  };

  const titleComponent = (parameter: IParameterDefinition) => (
    <div className="flex items-center w-full">
      <div className="tw-text-base flex items-center mr-3">
        <Tooltip message={`${t('overridable')}`}>
          <EqualChip label={parameter.overridable.toString()} />
        </Tooltip>
        <Tooltip message={t('type')}>
          <EqualChip label={parameter.type} />
        </Tooltip>
        <Tooltip message={t('name')}>
          <p className="mr-2 w-[200px]">{parameter.name}</p>
        </Tooltip>
      </div>
      {parameter.value && (
        <ColoredChip label={parameterValueLabel(parameter)} />
      )}
      {parameter.unit && (
        <Tooltip message={t('unit')}>
          <EqualChip label={parameter.unit} />
        </Tooltip>
      )}
      {parameter.format && (
        <Tooltip message={t('format')}>
          <EqualChip label={parameter.format} />
        </Tooltip>
      )}
    </div>
  );

  return (
    <div data-testid={dataTestId}>
      {parameters.map(parameter => {
        return (
          <ModelAttributeCard
            variant="LIGHT"
            key={slugify(parameter.name)}
            title={titleComponent(parameter)}
          ></ModelAttributeCard>
        );
      })}
    </div>
  );
}
